import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, X, Send } from "lucide-react";

/**
 * Chat lateral flutuante — Claude Sonnet 5 responde perguntas sobre relatórios agregados.
 * Persiste histórico via /api/ai/chat/history/{session_id}.
 */
export default function AiAssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => `session-${localStorage.getItem("user_id") || "anon"}-${Date.now()}`);
  const endRef = useRef(null);
  const [pos, setPos] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem("ai_widget_pos")); if (v && typeof v.x === "number") return v; } catch {}
    return { x: window.innerWidth - 80, y: window.innerHeight - 80 };
  });
  const drag = useRef(null);

  const clamp = (x, y) => ({
    x: Math.min(Math.max(8, x), window.innerWidth - 64),
    y: Math.min(Math.max(8, y), window.innerHeight - 64),
  });

  useEffect(() => {
    const onResize = () => setPos((p) => clamp(p.x, p.y));
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onPointerDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < 5) return;
    d.moved = true;
    setPos(clamp(d.ox + dx, d.oy + dy));
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.moved) localStorage.setItem("ai_widget_pos", JSON.stringify(pos));
    else setOpen((o) => !o);
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  // Não mostrar em telas públicas (login, etc) — verifica DEPOIS dos hooks
  const authed = typeof window !== "undefined" && !!localStorage.getItem("serenata_token");
  if (!authed) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const r = await api.post("/ai/chat", {
        session_id: sessionId,
        message: text,
      });
      setMessages((m) => [...m, { role: "assistant", content: r.data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `Erro: ${e?.response?.data?.detail || e.message}` }]);
    } finally { setBusy(false); }
  };

  const panelStyle = () => {
    const w = Math.min(380, window.innerWidth * 0.95);
    const h = Math.min(520, window.innerHeight * 0.8);
    const left = Math.min(Math.max(8, pos.x + 56 - w), window.innerWidth - w - 8);
    const above = pos.y - h - 12;
    const top = above >= 8 ? above : Math.min(pos.y + 68, window.innerHeight - h - 8);
    return { left: pos.x + 28 < window.innerWidth / 2 ? Math.min(pos.x, window.innerWidth - w - 8) : left, top: Math.max(8, top) };
  };

  return (
    <>
      {/* Botão flutuante */}
      <button
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{ left: pos.x, top: pos.y, touchAction: "none" }}
        className="fixed z-50 w-14 h-14 rounded-full bg-gradient-to-br from-brand-500 to-emerald-600 text-white shadow-lg hover:scale-105 transition-transform flex items-center justify-center cursor-grab active:cursor-grabbing select-none"
        title="Assistente IA (arraste para mover)"
        data-testid="ai-widget-toggle"
      >
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </button>

      {/* Painel */}
      {open && (
        <div style={panelStyle()} className="fixed z-40 w-[380px] max-w-[95vw] h-[520px] max-h-[80vh] bg-white rounded-lg shadow-2xl border border-ink-200 flex flex-col overflow-hidden" data-testid="ai-widget-panel">
          <div className="p-3 bg-gradient-to-r from-brand-500 to-emerald-600 text-white flex items-center gap-2">
            <Sparkles size={16} />
            <div className="flex-1">
              <div className="text-sm font-bold">Assistente Serenata</div>
              <div className="text-[10px] opacity-80">Claude Sonnet 5 · dados agregados</div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-ink-50/30 text-sm">
            {messages.length === 0 && (
              <div className="text-ink-500 text-xs italic p-4 text-center">
                Pergunte sobre seus relatórios.<br/>
                Ex: "Qual foi meu lucro no período?", "Quem é o melhor fornecedor?", "Como está a margem por vendedor?"
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`p-2 rounded-md whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-brand-500 text-white ml-8"
                    : "bg-white border border-ink-200 mr-8"
                }`}
                data-testid={`msg-${m.role}-${i}`}
              >
                {m.content}
              </div>
            ))}
            {busy && (
              <div className="p-2 rounded-md bg-white border border-ink-200 mr-8 text-ink-500 italic">
                pensando<span className="inline-block animate-pulse">...</span>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-2 border-t border-ink-200 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Pergunte algo..."
              className="flex-1 rounded-md border border-ink-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              disabled={busy}
              data-testid="ai-widget-input"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              className="p-2 rounded-md bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50"
              data-testid="ai-widget-send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
